import { useState, type Dispatch, type SetStateAction } from 'react';
import { Save } from 'lucide-react';

interface AutoSaveControlProps {
  enabled: boolean;
  intervalSec: number;
  onEnabledChange: Dispatch<SetStateAction<boolean>>;
  onIntervalChange: Dispatch<SetStateAction<number>>;
}

/** The status-bar pill for autosave, with its on/off and interval popover. */
export function AutoSaveControl({
  enabled,
  intervalSec,
  onEnabledChange,
  onIntervalChange,
}: AutoSaveControlProps) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative flex items-center gap-1">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`flex items-center gap-1 px-2 py-1 rounded text-xs ${enabled ? 'text-green-700 bg-green-50' : 'text-gray-500 hover:bg-gray-100'}`}
        title="Auto-save settings"
        data-testid="autosave-toggle"
      >
        <Save size={11} />
        {enabled ? `Auto ${intervalSec}s` : 'Auto off'}
      </button>
      {open && (
        <div className="absolute bottom-full right-0 mb-1 bg-white border border-gray-200 rounded shadow-lg p-3 w-52 z-20 text-xs">
          <div className="flex items-center justify-between mb-2">
            <span className="font-medium text-gray-700">Auto-save</span>
            <button
              type="button"
              onClick={() => onEnabledChange((v) => !v)}
              className={`px-2 py-0.5 rounded text-xs font-medium ${enabled ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}
            >
              {enabled ? 'On' : 'Off'}
            </button>
          </div>
          <label className="block text-gray-600 mb-1" htmlFor="autosave-interval">
            Interval (seconds)
          </label>
          <input
            id="autosave-interval"
            type="number"
            min={10}
            max={300}
            step={10}
            value={intervalSec}
            onChange={(e) => onIntervalChange(Math.max(10, parseInt(e.target.value, 10) || 30))}
            className="w-full border border-gray-300 rounded px-2 py-1 text-xs"
          />
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="mt-2 w-full text-center text-gray-400 hover:text-gray-600"
          >
            Close
          </button>
        </div>
      )}
    </div>
  );
}
